/**
 * Runtime platform detection for print-method guidance.
 *
 * Lets the centralized printer config show "this device can use X" and
 * lets the dispatcher pick the best available transport per platform.
 * Pure browser/Capacitor detection — no native plugin required.
 */
export function isCapacitorNative() {
  try {
    const c = window.Capacitor;
    return !!(c && (c.isNativePlatform ? c.isNativePlatform() : c.isNative));
  } catch { return false; }
}

export function nativePlatform() {
  try {
    const c = window.Capacitor;
    if (c && c.getPlatform) return c.getPlatform(); // 'android' | 'ios' | 'web'
  } catch { /* noop */ }
  return 'web';
}

export function isIOS() {
  const ua = navigator.userAgent || '';
  const iOSUA = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as Mac; detect via touch points.
  const iPadDesktop = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOSUA || iPadDesktop || nativePlatform() === 'ios';
}

export function isAndroid() {
  return /Android/i.test(navigator.userAgent || '') || nativePlatform() === 'android';
}

/**
 * Returns a small descriptor used by the printer config UI to nudge the
 * user toward connection methods that actually work on their device.
 */
export function detectDevice() {
  const native = isCapacitorNative();
  if (native && nativePlatform() === 'android') {
    return { key: 'android_app', label: 'SmartoERP Android app',
             recommended: ['bluetooth', 'usb', 'network'],
             note: 'Bluetooth, USB and network printers are all available in the native app.' };
  }
  if (isIOS()) {
    return { key: 'ios', label: 'iPad / iPhone (iOS)',
             recommended: ['airprint', 'network'],
             note: 'On iPad/iOS use AirPrint or a network (LAN/Wi-Fi) ESC/POS printer. Bluetooth/USB require a native bridge.' };
  }
  if (isAndroid()) {
    return { key: 'android_web', label: 'Android browser',
             recommended: ['network', 'browser'],
             note: 'In the browser, use a network printer or the OS print dialog. Install the SmartoERP Android app for Bluetooth/USB.' };
  }
  return { key: 'desktop', label: 'Desktop / Windows browser',
           recommended: ['windows', 'network', 'browser'],
           note: 'On Windows use the Print Bridge (silent) or a network printer. The browser dialog always works as a fallback.' };
}

export const CONNECTION_METHODS = [
  { value: 'network', label: 'Network / LAN / Wi-Fi (ESC/POS · ePOS)', platforms: 'All devices' },
  { value: 'airprint', label: 'AirPrint (iPad / iOS)', platforms: 'iPad / iOS' },
  { value: 'windows', label: 'Windows Print Bridge', platforms: 'Windows desktop' },
  { value: 'browser', label: 'Browser print dialog', platforms: 'Any browser' },
  { value: 'bluetooth', label: 'Bluetooth (Android app)', platforms: 'SmartoERP Android app' },
  { value: 'usb', label: 'USB (Android app)', platforms: 'SmartoERP Android app' },
  { value: 'sunmi', label: 'Sunmi built-in printer', platforms: 'Sunmi devices (app)' },
];
